export enum StorageKeys {
  MESSAGE_LIMIT_KEY = "chatMessageCount",
  FCM_TOKEN = "fcmToken",
  IS_IN_TRIAL_MODE = "IS_IN_TRIAL_MODE",
  PROFILE_DATA = "PROFILE_DATA",
  SELECTED_STATE_CODE = "SELECTED_STATE_CODE",
  PURCHASE_STATE = "PURCHASE_STATE",
  QUESTIONS_KEY = "questions",
  DELETED_QUESTIONS_KEY = "deleted_questions",
  QUESTION_USE_FREQUENCY = "question_freq",
  STATE_INFO = "state_info",
  CURRICULUM = "curriculum",
  TYPES_MIN_MAX = "types_min_max",
  TESTS_KEY = "tests",
  PRACTICE_DAYS_KEY = "practice_days",
  SUBSECTION_NAMES_KEY = "subsection_names",
  HAVE_SHOWN_NOTIFICATION_PROMPT = "haveShownNotificationPrompt",
  HAVE_SHOWN_TOOLTIP = "haveShownTooltip",
  ACTIVE_TEST_KEY = "activeTest",
  DELETED_TEST_IDS = "deleted_test_ids",
  EMAIL_PREFERENCES = "emailPreferences",
  SELECTED_LANGUAGE = "selected_language",
}

export const ALL_STORAGE_KEYS = Array.from(Object.values(StorageKeys));

/**
 * Keys written outside the `StorageKeys` enum. They are captured *before* the
 * user signs in (from a redemption or referral link) and consumed afterwards,
 * so they cannot be namespaced by user id — but they must not survive a
 * logout, or the next person to sign in on this browser redeems the previous
 * person's organisation seat or referral credit.
 */
const PRE_AUTH_PENDING_KEYS = [
  "pending_access_code",
  "pending_access_code_meta",
  "pending_referral_code",
  "pending_referral_meta",
] as const;

/**
 * Device- or preference-scoped keys that intentionally outlive a logout.
 * `DEVICE_ID` identifies the browser, not the person, and language is a UI
 * preference that would be jarring to reset on sign-out.
 */
const KEYS_PRESERVED_ACROSS_LOGOUT: string[] = [
  StorageKeys.SELECTED_LANGUAGE,
];

/**
 * Everything owned by the signed-in user. Cleared on sign-out.
 *
 * None of the app's storage keys are namespaced by user id, so without this
 * sweep an unfinished exam, question-frequency map, cached profile, or pending
 * redemption code written by one account is silently adopted by the next
 * account to sign in on the same browser.
 */
export function clearUserScopedStorage(): void {
  // Every access is guarded individually. `localStorage` throws SecurityError
  // when storage is blocked (Safari "block all cookies", locked-down enterprise
  // profiles, some in-app webviews). This runs immediately before sign-out, so
  // an unguarded throw would abort `signOut()` and leave the user authenticated
  // — turning a privacy gap into an auth failure.
  const remove = (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable; nothing was persisted, so nothing can leak.
    }
  };

  for (const key of ALL_STORAGE_KEYS) {
    if (KEYS_PRESERVED_ACROSS_LOGOUT.includes(key)) continue;
    remove(key);
  }

  for (const key of PRE_AUTH_PENDING_KEYS) {
    remove(key);
  }
}
